import {
  DarkModeRounded as DarkModeIcon,
  LightModeRounded as LightModeIcon,
} from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppTheme } from '@/context/app-theme-context';
import { useUserProfile } from '@/firebase/hooks/use-user-profile';
import { useToast } from '@/hooks/use-toast';
import { getNextAppThemeMode } from '@/lib/theme/mode';

const CLOUD_SAVE_BUSY_WINDOW_MS = 800;

interface ThemeModeToggleProps {
  placement: 'header' | 'account-menu';
}

export function ThemeModeToggle({ placement }: ThemeModeToggleProps) {
  const { t } = useTranslation();
  const { mode, setMode } = useAppTheme();
  const { updateProfile } = useUserProfile();
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const saveRequestId = useRef(0);

  const nextMode = getNextAppThemeMode(mode);
  const label = t(
    nextMode === 'light' ? 'header.switch_to_light_mode' : 'header.switch_to_dark_mode',
  );
  const Icon = nextMode === 'light' ? LightModeIcon : DarkModeIcon;

  const handleToggle = async () => {
    if (isSaving) return;

    const previousMode = mode;
    const requestId = ++saveRequestId.current;
    setMode(nextMode);
    setIsSaving(true);

    // Firestore queues writes while offline. Keep the control responsive even
    // when the remote acknowledgement has not arrived yet.
    const busyTimer = window.setTimeout(() => {
      if (saveRequestId.current === requestId) setIsSaving(false);
    }, CLOUD_SAVE_BUSY_WINDOW_MS);

    try {
      await updateProfile({ themeMode: nextMode });
    } catch (error) {
      console.error('Failed to save theme preference:', error);
      if (saveRequestId.current === requestId) {
        setMode(previousMode);
        toast({
          title: t('profile.appearanceSaveError'),
          variant: 'destructive',
        });
      }
    } finally {
      window.clearTimeout(busyTimer);
      if (saveRequestId.current === requestId) setIsSaving(false);
    }
  };

  if (placement === 'account-menu') {
    return (
      <MenuItem
        onClick={handleToggle}
        disabled={isSaving}
        aria-label={label}
        sx={{
          display: { xs: 'flex', md: 'none' },
          py: 1,
          fontSize: '0.875rem',
        }}
      >
        <ListItemIcon>
          {isSaving ? <CircularProgress size={18} color="inherit" /> : <Icon sx={{ fontSize: 18 }} />}
        </ListItemIcon>
        {label}
      </MenuItem>
    );
  }

  return (
    <Tooltip title={label} enterDelay={400}>
      <span>
        <IconButton
          id="header-theme-mode-toggle"
          size="small"
          onClick={handleToggle}
          disabled={isSaving}
          aria-label={label}
          sx={{
            display: { xs: 'none', md: 'inline-flex' },
            color: 'text.secondary',
            '&:hover': { color: 'primary.main' },
          }}
        >
          {isSaving ? <CircularProgress size={18} color="inherit" /> : <Icon sx={{ fontSize: 20 }} />}
        </IconButton>
      </span>
    </Tooltip>
  );
}
